import React, { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import {
  getRacks,
  createRack,
  updateRack,
  deleteRack,
} from "../api/rackService";
import "../styles/ItemModels.css";
import TableComponent from "../pages/TableComponent";
import Pagination from "../view/common/pagination/Pagination";
import { Rack } from "../models/rack";
import "../styles/common/sub-menu.css";
import "../styles/common/spinner.css";
import "../styles/common/icon-style.css";
import "../styles/ItemModels.css";
import "../styles/common/toplayout.css";
import "../styles/common/btn.css";
import Select from "react-select";
import CustomAlert from "../view/common/alert/CustomAlert";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/slices/store";
import { resetFilters, updateFilters } from "../redux/slices/filterSlice";
import { customStyles } from '../styles/common/customFormStyles';
import { getData } from "../api/ApiServices";

const RackComponent: React.FC = () => {
  const [racks, setRacks] = useState<Rack[]>([]);
  const [currentRack, setCurrentRack] = useState<Rack>({
    rackName: "",
    status: "ACTIVE",
  });
  const [editMode, setEditMode] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  // Add state to track if a submission is in progress
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [originalRack, setOriginalRack] = useState<Rack>({
    rackName: "",
    status: "ACTIVE",
  });

  const [sortBy, setSortBy] = useState<string>("rackName");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const [validationErrors, setValidationErrors] = useState<{
    rackName?: string;
    status?: string;
  }>({});

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertConfirmAction, setAlertConfirmAction] = useState<() => void>(
    () => { }
  );
  const [alertCancelAction, setAlertCancelAction] = useState<() => void>(
    () => { }
  );
  const [showConfirmButton, setShowConfirmButton] = useState(true);
  const [showCancelButton, setShowCancelButton] = useState(true);
  const [confirmButtonText, setConfirmButtonText] = useState("OK");
  const [cancelButtonText, setCancelButtonText] = useState("Cancel");
  const [autoCloseDelay, setAutoCloseDelay] = useState<number | null>(null);
  const excludeFieldsForRacks = ["createdBy"];

  const dispatch = useDispatch();
  const filters = useSelector((state: RootState) => state.filters);
  const permission = useSelector((state: RootState) => state.permissions)

  const [initializeRack, isRackInitialized] = useState(false);

  useEffect(() => {
    dispatch(resetFilters());
    isRackInitialized(true);
  }, [dispatch]);

  useEffect(() => {
    if (initializeRack) {
      loadData();
    }
  }, [initializeRack, currentPage, itemsPerPage, searchTerm, filters, sortBy, sortOrder]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const response: any = await getData("/racks", {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        filter: filters,
        sortBy: sortBy,
        sortOrder: sortOrder,
      });
      console.log(response);

      if (response.data) {
        setRacks(response.data?.data || []);
        setTotalItems(response?.data?.pagination.total);
        setTotalPages(response?.data?.pagination.totalPages);
      } else if (response.error) {
        setError(response.error);
      }
    } catch (err) {
      setError("Failed to load racks");
    }
    setIsLoading(false);
  };

  const closeAlert = () => {
    setShowAlert(false);
  };

  const configureAlert = (
    message: string,
    confirmAction: () => void,
    cancelAction: () => void = () => { },
    confirmText: string = "OK",
    cancelText: string = "Cancel",
    showConfirm: boolean = true,
    showCancel: boolean = true,
    autoClose: number | null = null
  ) => {
    setAlertMessage(message);
    setAlertConfirmAction(() => confirmAction);
    setAlertCancelAction(() => cancelAction);
    setConfirmButtonText(confirmText);
    setCancelButtonText(cancelText);
    setShowConfirmButton(showConfirm);
    setShowCancelButton(showCancel);
    setAutoCloseDelay(autoClose);
    setShowAlert(true);
  };

  const hasChanges = (): boolean => {
    return (
      currentRack.rackName !== originalRack.rackName ||
      currentRack.status !== originalRack.status
    );
  };

  const checkForDuplicates = (): boolean => {
    const existingRacks = editMode
      ? racks.filter((rack) => rack._id !== currentRack._id)
      : racks;

    const duplicateName = existingRacks.find(
      (rack) =>
        rack.rackName.toLowerCase() === currentRack.rackName.toLowerCase()
    );

    const errors: { rackName?: string } = { ...validationErrors };

    if (duplicateName) {
      errors.rackName = "Rack name already exists";
    }

    setValidationErrors(errors);

    if (duplicateName) {
      configureAlert(
        "Duplicate rack name!",
        closeAlert,
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000
      );
      return true;
    }

    return false;
  };

  // Validate form fields
  const validateForm = (): boolean => {
    const errors: { rackName?: string; status?: string } = {};

    if (!currentRack.rackName.trim()) {
      errors.rackName = "Rack name is required";
    }

    if (editMode && !currentRack.status) {
      errors.status = "Status is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle sort change
  const handleSortChange = (
    sortByField: string,
    sortOrderDirection: "asc" | "desc"
  ) => {
    setSortBy(sortByField);
    setSortOrder(sortOrderDirection);
  };

  const handleAddRack = () => {
    setCurrentRack({ rackName: "", status: "ACTIVE" });
    setOriginalRack({ rackName: "", status: "ACTIVE" });
    setValidationErrors({}); // Clear any previous validation errors
    setEditMode(false);
    setIsModalOpen(true);
  };

  const handleEdit = (rack: Rack) => {
    setCurrentRack({ ...rack });
    setOriginalRack({ ...rack });
    setValidationErrors({});
    setEditMode(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    const rackToDelete = racks.find((rack) => rack._id === id);

    if (rackToDelete) {
      configureAlert(
        `Are you sure you want to Inactive ${rackToDelete.rackName}?`,
        async () => {
          closeAlert();

          try {
            await deleteRack(id);
            await loadData();

            configureAlert(
              `Rack has been Inactive successfully.`,
              () => {
                closeAlert();
              },
              closeAlert,
              "OK",
              "",
              true,
              false,
              3000
            );
          } catch (error) {
            console.error("Error deleting rack:", error);
            configureAlert(
              "Failed to delete rack.",
              closeAlert,
              closeAlert,
              "OK",
              "",
              true,
              false,
              3000
            );
          }
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null
      );
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent multiple submissions
    if (isSubmitting) return;

    if (!validateForm()) {
      return;
    }

    if (editMode && !hasChanges()) {
      setIsModalOpen(false);

      configureAlert(
        "No changes were made to update.",
        () => {
          closeAlert();
        },
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000
      );
      return;
    }

    if (checkForDuplicates()) {
      return;
    }

    // Set both loading and submitting states
    setIsLoading(true);
    setIsSubmitting(true);
    
    try {
      let updatedRack = { ...currentRack };

      if (editMode) {
        if (currentRack?.status === "INACTIVE") {
          updatedRack.status = "ACTIVE";
        }

        await updateRack(currentRack._id as string, updatedRack);
        setIsModalOpen(false);

        configureAlert(
          `${currentRack.rackName} has been updated successfully.`,
          () => {
            loadData();
            closeAlert();
          },
          closeAlert,
          "OK",
          "",
          true,
          false,
          3000
        );
      } else {
        await createRack(updatedRack);
        setIsModalOpen(false);

        configureAlert(
          `${currentRack.rackName} has been added successfully.`,
          () => {
            loadData();
            closeAlert();
          },
          closeAlert,
          "OK",
          "",
          true,
          false,
          3000
        );
      }
    } catch (error) {
      console.error("Error saving rack:", error);
      configureAlert(
        `Failed to ${editMode ? "update" : "add"} rack.`,
        closeAlert,
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000
      );
    } finally {
      // Reset both loading and submitting states
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    if (
      isModalOpen &&
      (editMode ? hasChanges() : currentRack.rackName.trim() !== "")
    ) {
      configureAlert(
        "Are you sure you want to close without saving?",
        () => {
          setIsModalOpen(false);
          closeAlert();
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null
      );
    } else {
      setIsModalOpen(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentRack((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSelectChange = (selectedOption: any, { name }: any) => {
    setCurrentRack((prev) => ({
      ...prev,
      [name]: selectedOption?.value || "",
    }));

    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const columns = [
    { key: "rackName", label: "Rack Name", sortable: true },
    { key: "status", label: "Status", sortable: true },
  ];

  const statusOptions = [
    { value: "ACTIVE", label: "ACTIVE" },
    { value: "INACTIVE", label: "INACTIVE" },
  ];

  return (
    <div className="item-models-container">
      <style>{customStyles}</style>

      <div className="item-models-header">
        <div className="border">
          <div className="start-end-layout">
            <div className="breadcrumb-nav">
              <span>Master Item</span>
              <span>Racks</span>
            </div>
            {permission.menus.racks.create ?
              <div className="items-button-container tooltip-wrapper">
                <button className="item-models-add-btn" onClick={handleAddRack}>
                  <Plus size={16} />
                </button>
              </div>
              : <></>}
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {isLoading ? (
        <div className="spinner"></div>
      ) : (
        <div className="box-container">
          <TableComponent
            columns={columns}
            data={racks}
            onEdit={handleEdit}
            onDelete={handleDelete}
            setFilter={(filters: Record<string, any>) => {
              Object.entries(filters).forEach(([key, value]) => {
                dispatch(updateFilters({ key, value: String(value) }));
              });
            }}
            setSorting={handleSortChange}
            defaultSortConfig={{ key: sortBy, direction: sortOrder }}
            excludeFields={excludeFieldsForRacks}
            context="racks"
          />
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            totalItems={totalItems}
          />
        </div>
      )}

      {isModalOpen && (
        <div className="item-models-modal-overlay">
          <div className="item-models-modal">
            <div className="item-models-modal-header">
              <h5>{editMode ? "Edit Rack" : "Add New Rack"}</h5>
              <button
                className="item-models-modal-close"
                onClick={handleModalClose}
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSave} noValidate>
              <div className="form-group">
                <input
                  type="text"
                  name="rackName"
                  placeholder="Rack Name"
                  value={currentRack.rackName}
                  onChange={handleInputChange}
                  className={validationErrors.rackName ? "input-error" : ""}
                  required
                />
                {validationErrors.rackName && (
                  <div className="form-error-message">
                    {validationErrors.rackName}
                  </div>
                )}
              </div>

              {editMode && (
                <div className="select-container">
                  <Select
                    name="status"
                    options={statusOptions}
                    value={statusOptions.find(
                      (option) => option.value === currentRack.status
                    )}
                    onChange={handleSelectChange}
                    placeholder="Select Status"
                    className={validationErrors.status ? "select-error" : ""}
                    classNamePrefix="react-select"
                  />
                  {validationErrors.status && (
                    <div className="form-error-message">
                      {validationErrors.status}
                    </div>
                  )}
                </div>
              )}
              <button
                type="submit"
                disabled={isLoading || isSubmitting}
                className="submit-button"
              >
                {isLoading || isSubmitting ? "Saving..." : editMode ? "Update" : "Add"}
              </button>
            </form>
          </div>
        </div>
      )}
      {showAlert && (
        <CustomAlert
          message={alertMessage}
          onConfirm={alertConfirmAction}
          onCancel={alertCancelAction}
          confirmText={confirmButtonText}
          cancelText={cancelButtonText}
          showConfirmButton={showConfirmButton}
          showCancelButton={showCancelButton}
          autoCloseDelay={autoCloseDelay}
        />
      )}
    </div>
  );
};

export default RackComponent;